import React, { useEffect, useState } from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import {
  Button,
  TextField,
  Grid,
  Typography,
  CircularProgress,
} from "@mui/material";
import dayjs from "dayjs";
import {
  addShipment,
  getShipmentDetail,
  updateShipment,
} from "../../api/imports";
import { useSnack } from "../../services/SnakbarProvider";
import { handleError } from "../../services/errorHandler";
import { getProviderList } from "../../api/providers";
import { exportPO } from "../../api/purchaseOrders";
import MyDatePicker from "../../atoms/Common/MyDatePicker";
import LGAutocomplete from "../../atoms/Common/LGAutocomplete.tsx";
import { handleDownload as downloadPDF } from "../../services/PDFHandler.ts";
import ShipmentPurchaseOrderFields from "./ShipmentPurchaseOrderFields";

const validationSchema = Yup.object().shape({
  ref: Yup.string().required("Referencia es obligatoria"),
  portArrivalDate: Yup.date().required(
    "Fecha estimada de arribo es obligatoria"
  ),
  providerId: Yup.string().required("Seleccione un proveedor"),
  containers: Yup.array().of(
    Yup.object().shape({
      code: Yup.string(),
      deliveryDate: Yup.date().nullable(),
    })
  ),
});

const initialValues = {
  ref: "",
  portArrivalDate: null,
  providerId: "",
  purchaseOrders: [],
  containers: [{ code: "" }],
};

const ShipmentForm = ({ onSubmit, id, companyId }) => {
  const formMode = id ? "edit" : "insert";
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState(initialValues);
  const [purchaseOrders, setPurchaseOrders] = useState([]);

  const { openSnack } = useSnack();

  const fetchData = async () => {
    try {
      setLoading(true);
      const res = await getShipmentDetail(id);
      if (res.data.plantArrivalDate == "0001-01-01") {
        res.data.plantArrivalDate = dayjs();
      }
      setFormData(res.data);
      await fetchProviderOptions(res.data.provider.code);
    } catch (e) {
      openSnack(handleError(e), "error");
    }
    setLoading(false);
  };

  const fetchProviderOptions = async (params) => {
    try {
      const res = await getProviderList({
        code: params?.code || null,
      });
      return res;
    } catch (e) {
      openSnack(handleError(e), "error");
      return { data: [] };
    }
  };

  const handleProviderChange = (value) => {
    setFormData({ ...formData, providerId: value ? value.id : "" });
  };

  const handleDownloadPO = async (purchaseOrderId) => {
    try {
      const response = await exportPO(purchaseOrderId, true);
      downloadPDF(
        response,
        "ordencompra",
        "No se pudo generar el PDF, verifica que el proveedor tenga contacto y dirección",
        (error) => {
          openSnack(
            "No se pudo generar el PDF, verifica que el proveedor tenga contacto y dirección",
            "error"
          );
        }
      );
    } catch (e) {
      openSnack(
        "No se pudo generar el PDF, verifica que el proveedor tenga contacto y dirección",
        "error"
      );
    }
  };

  const handleAddPurchaseOrder = (purchaseOrder) => {
    setPurchaseOrders((prev) => [...prev, purchaseOrder]);
  };

  const handleRemovePurchaseOrder = (purchaseOrderId) => {
    setPurchaseOrders((prev) =>
      prev.filter((po) => po.purchaseOrderId !== purchaseOrderId),
    );
  };

  const handleSubmit = async (values) => {
    const formattedDate = values.portArrivalDate
      ? dayjs(values.portArrivalDate).format("YYYY-MM-DD")
      : "";
    values.portArrivalDate = formattedDate;
    values.companyId = companyId;

    if (formMode == "insert") {
      const payload = {
        shipmentData: {
          companyId: values.companyId,
          providerId: values.providerId,
          ref: values.ref,
          portArrivalDate: values.portArrivalDate,
        },
        purchaseOrderIds: purchaseOrders
          .map((po) => po.purchaseOrderId)
          .filter((poId) => poId),
        containers: values.containers,
      };

      try {
        await addShipment(payload);
        openSnack("Embarque guardado exitosamente", "success");
        onSubmit();
      } catch (e) {
        openSnack(handleError(e), "error");
      }
    } else {
      const payload = {
        shipmentData: { ...values },
        containers: values.containers,
      };
      try {
        await updateShipment(id, payload);
        openSnack("Embarque actualizado exitosamente", "success");
        onSubmit();
      } catch (e) {
        console.log(e);

        openSnack(handleError(e), "error");
      }
    }
  };

  useEffect(() => {
    if (formMode == "edit") {
      fetchData();
    } else {
      setFormData(initialValues);
      setPurchaseOrders([]);
    }
  }, []);

  return loading ? (
    <CircularProgress />
  ) : (
    <Formik
      enableReinitialize
      initialValues={{
        ...formData,
      }}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({
        values,
        handleChange,
        handleBlur,
        setFieldValue,
        errors,
        touched,
      }) => (
        <Form>
          <Grid container spacing={2} alignItems={"center"}>
            <Grid container spacing={2} item xs={6} pr={2}>
              <Grid item xs={12}>
                <Typography variant="h4">Datos Generales</Typography>
              </Grid>
              <Grid item xs={12}>
                <LGAutocomplete
                  id="providerId"
                  name="providerId"
                  fetchFunction={getProviderList}
                  queryParams={{
                    companyId: companyId,
                  }}
                  optionLabel="code"
                  onChange={(e, value) => {
                    handleProviderChange(value);
                  }}
                  label="Proveedor"
                  error={
                    touched?.providerId &&
                    Boolean(errors?.providerId)
                  }
                  helperText={
                    touched?.providerId &&
                    errors?.providerId
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <ShipmentPurchaseOrderFields
                  companyId={companyId}
                  purchaseOrders={purchaseOrders}
                  onAdd={handleAddPurchaseOrder}
                  onRemove={handleRemovePurchaseOrder}
                  onDownload={handleDownloadPO}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  id="ref"
                  name="ref"
                  label="Referencia"
                  value={values.ref}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.ref && Boolean(errors.ref)}
                  helperText={touched.ref && errors.ref}
                />
              </Grid>
              <Grid item xs={12}>
                <MyDatePicker
                  fullWidth
                  slotProps={{ textField: { fullWidth: true } }}
                  label="Fecha Estimada a Puerto"
                  value={
                    values.portArrivalDate
                      ? dayjs(values.portArrivalDate)
                      : null
                  }
                  onChange={(value) => setFieldValue("portArrivalDate", value)}
                  renderInput={(params) => <TextField {...params} fullWidth />}
                />
              </Grid>
            </Grid>
            <Grid item xs={12}>
              <Button
                color="primary"
                variant="contained"
                fullWidth
                type="submit"
              >
                Aceptar
              </Button>
            </Grid>
          </Grid>
        </Form>
      )}
    </Formik>
  );
};

export default ShipmentForm;
